import type {
  PlacaProduct,
  PlacaQuote,
  PlacaQuoteCustomItem,
  PlacaQuoteExtraItem,
  CreatePlacaProductRequest,
} from '../types/placas';
import { apiRequest } from './api-client';

const PLACAS = '/backoffice/placas';

// ===== Products =====

export async function fetchProducts(token: string, params?: { search?: string; includeInactive?: boolean }): Promise<PlacaProduct[]> {
  const query = new URLSearchParams();
  if (params?.search) query.set('search', params.search);
  if (params?.includeInactive) query.set('includeInactive', 'true');
  return apiRequest(`${PLACAS}/products?${query}`, token);
}

export async function fetchProduct(id: string, token: string): Promise<PlacaProduct> {
  return apiRequest(`${PLACAS}/products/${id}`, token);
}

export async function createProduct(data: CreatePlacaProductRequest, token: string): Promise<PlacaProduct> {
  return apiRequest(`${PLACAS}/products`, token, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateProduct(id: string, data: Partial<CreatePlacaProductRequest>, token: string): Promise<PlacaProduct> {
  return apiRequest(`${PLACAS}/products/${id}`, token, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteProduct(id: string, token: string): Promise<void> {
  await apiRequest(`${PLACAS}/products/${id}`, token, { method: 'DELETE' });
}

export async function toggleProductStatus(id: string, token: string): Promise<PlacaProduct> {
  return apiRequest(`${PLACAS}/products/${id}/status`, token, { method: 'PATCH' });
}

// ===== Quotes =====

export async function fetchQuotes(token: string, params?: { search?: string; status?: string; startDate?: string; endDate?: string }): Promise<PlacaQuote[]> {
  const query = new URLSearchParams();
  if (params?.search) query.set('search', params.search);
  if (params?.status) query.set('status', params.status);
  if (params?.startDate) query.set('startDate', params.startDate);
  if (params?.endDate) query.set('endDate', params.endDate);
  return apiRequest(`${PLACAS}/quotes?${query}`, token);
}

export async function fetchQuote(id: string, token: string): Promise<PlacaQuote> {
  return apiRequest(`${PLACAS}/quotes/${id}`, token);
}

export interface CreatePlacaQuoteRequest {
  customerName?: string;
  customerEmail?: string;
  customerPhone?: string;
  customerCompany?: string;
  customerDocument?: string;
  customerSegment?: string;
  notes?: string;
}

export async function createQuote(data: CreatePlacaQuoteRequest, token: string): Promise<PlacaQuote> {
  return apiRequest(`${PLACAS}/quotes`, token, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateQuoteStatus(id: string, status: string, token: string): Promise<PlacaQuote> {
  return apiRequest(`${PLACAS}/quotes/${id}/status`, token, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });
}

export async function markPlacaQuoteAsViewed(quoteId: string, token: string): Promise<PlacaQuote> {
  return apiRequest(`${PLACAS}/quotes/${quoteId}/viewed`, token, {
    method: 'POST',
  });
}

// ===== Quote Item Management =====

export async function addQuoteItem(quoteId: string, data: { productId: string; quantity: number; dimensionId?: string }, token: string): Promise<any> {
  return apiRequest(`${PLACAS}/quotes/${quoteId}/items`, token, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateQuoteItem(itemId: string, data: { quantity: number }, token: string): Promise<any> {
  return apiRequest(`${PLACAS}/quotes/items/${itemId}`, token, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function deleteQuoteItem(itemId: string, token: string): Promise<void> {
  await apiRequest(`${PLACAS}/quotes/items/${itemId}`, token, { method: 'DELETE' });
}

// ===== Custom Items =====

export async function fetchPendingCustomItems(token: string): Promise<PlacaQuoteCustomItem[]> {
  return apiRequest(`${PLACAS}/quotes/pending-custom-items`, token);
}

export interface ConfirmCustomItemPricePayload {
  confirmedPrice?: number;
}

export async function confirmCustomItemPrice(itemId: string, payload: ConfirmCustomItemPricePayload, token: string): Promise<PlacaQuoteCustomItem> {
  return apiRequest(`${PLACAS}/quotes/custom-items/${itemId}/confirm-price`, token, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function deleteCustomItem(itemId: string, token: string): Promise<void> {
  await apiRequest(`${PLACAS}/quotes/custom-items/${itemId}`, token, { method: 'DELETE' });
}

// ===== Quote Extra Items =====

export async function addExtraItem(quoteId: string, description: string, value: number, token: string): Promise<PlacaQuoteExtraItem> {
  return apiRequest(`${PLACAS}/quotes/${quoteId}/extra-items`, token, {
    method: 'POST',
    body: JSON.stringify({ description, value }),
  });
}

export async function deleteExtraItem(itemId: string, token: string): Promise<void> {
  await apiRequest(`${PLACAS}/quotes/extra-items/${itemId}`, token, { method: 'DELETE' });
}

export async function updateExtraItems(
  quoteId: string,
  items: Array<{ description: string; value: number }>,
  token: string
): Promise<void> {
  await apiRequest(`${PLACAS}/quotes/${quoteId}/extra-items`, token, {
    method: 'PUT',
    body: JSON.stringify(items),
  });
}

// ===== Quote Discount =====

export async function updateQuoteDiscount(quoteId: string, discount: string, token: string): Promise<PlacaQuote> {
  return apiRequest(`${PLACAS}/quotes/${quoteId}/discount`, token, {
    method: 'PATCH',
    body: JSON.stringify({ discount }),
  });
}
